import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import StoryCard from "@/components/feature/StoryCard";
import { categories, stories } from "@/mocks/stories";

export default function StoriesExplorer() {
  const [params] = useSearchParams();
  const initialCategory = params.get("category") || "all";
  const initialQuery = params.get("q") || "";

  const [category, setCategory] = useState(initialCategory);
  const [query, setQuery] = useState(initialQuery);
  const [sort, setSort] = useState<"latest" | "hot">("latest");

  const filtered = useMemo(() => {
    const kw = query.trim().toLowerCase();
    const list = stories.filter((story) => {
      const matchCategory = category === "all" || story.categoryId === category;
      const matchQuery =
        !kw ||
        story.title.toLowerCase().includes(kw) ||
        story.excerpt.toLowerCase().includes(kw) ||
        story.category.toLowerCase().includes(kw) ||
        story.tags.some((tag) => tag.toLowerCase().includes(kw));
      return matchCategory && matchQuery;
    });
    if (sort === "hot") {
      return [...list].sort((a, b) => b.heat - a.heat);
    }
    return [...list].sort((a, b) => (a.date < b.date ? 1 : -1));
  }, [category, query, sort]);

  const activeCategory = categories.find((c) => c.id === category);

  return (
    <div>
      <div className="flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 md:flex-row md:items-center md:justify-between md:p-5">
        <label className="relative flex w-full items-center md:max-w-sm">
          <i className="ri-search-line absolute left-3 text-lg text-foreground-400" aria-hidden="true"></i>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="搜索标题、栏目或关键词标签"
            aria-label="搜索爆料"
            className="w-full rounded-md border border-background-300 bg-background-100 py-2.5 pl-10 pr-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50"
          />
        </label>

        <div className="flex items-center gap-2">
          <span className="text-xs font-label text-foreground-500">排序</span>
          <div className="flex rounded-full border border-background-300 bg-background-100 p-1">
            <button
              type="button"
              onClick={() => setSort("latest")}
              className={`rounded-full px-3.5 py-1.5 text-xs font-label transition-colors cursor-pointer ${
                sort === "latest" ? "bg-primary-600 text-background-50" : "text-foreground-700 hover:text-primary-700"
              }`}
            >
              最新发布
            </button>
            <button
              type="button"
              onClick={() => setSort("hot")}
              className={`rounded-full px-3.5 py-1.5 text-xs font-label transition-colors cursor-pointer ${
                sort === "hot" ? "bg-primary-600 text-background-50" : "text-foreground-700 hover:text-primary-700"
              }`}
            >
              热度优先
            </button>
          </div>
        </div>
      </div>

      <div className="mt-5 flex flex-wrap gap-2">
        {categories.map((cat) => {
          const isActive = category === cat.id;
          const count =
            cat.id === "all" ? stories.length : stories.filter((s) => s.categoryId === cat.id).length;
          return (
            <button
              key={cat.id}
              type="button"
              onClick={() => setCategory(cat.id)}
              className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-label transition-colors cursor-pointer ${
                isActive
                  ? "border-primary-600 bg-primary-600 text-background-50"
                  : "border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
              }`}
            >
              {cat.name}
              <span
                className={`rounded-full px-1.5 text-xs ${
                  isActive ? "bg-background-50/25 text-background-50" : "bg-background-200 text-foreground-600"
                }`}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      <p className="mt-5 text-sm font-label text-foreground-500">
        共找到 <span className="font-semibold text-foreground-900">{filtered.length}</span> 篇稿件
        {activeCategory && category !== "all" && <> · 当前栏目：{activeCategory.name}</>}
        {query && <> · 关键词：{query}</>}
      </p>

      {filtered.length > 0 ? (
        <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {filtered.map((story) => (
            <StoryCard key={story.id} story={story} />
          ))}
        </div>
      ) : (
        <div className="mt-6 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-search-eye-line text-3xl" aria-hidden="true"></i>
          </span>
          <h3 className="mt-4 font-heading text-lg font-bold text-foreground-900">没有匹配的稿件</h3>
          <p className="mt-2 max-w-sm text-sm text-foreground-600">
            换个关键词，或者选择其他栏目试试。如果这条线索你也知道一些，欢迎匿名投递给我们。
          </p>
          <button
            type="button"
            onClick={() => {
              setCategory("all");
              setQuery("");
            }}
            className="mt-5 inline-flex items-center gap-2 rounded-md bg-primary-600 px-5 py-2.5 text-sm font-label text-background-50 transition-colors hover:bg-primary-700 cursor-pointer"
          >
            <i className="ri-refresh-line" aria-hidden="true"></i>
            重置筛选
          </button>
        </div>
      )}
    </div>
  );
}